import type { CSSProperties } from 'react';
import type { Element, TextElement } from '@/components/canvas/document/publicTypes';
import { TextElement as TextElementView } from '@/components/canvas/elements/text/TextElement';
import { getBuiltinElementComponents } from '@/components/canvas/elements/registry/builtinElements';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';

/** 工作区元素整体叠在页板之上（与元素 layout.zIndex 相加） */
const WORKSPACE_ELEMENTS_BASE_Z_INDEX = 1000;

export interface WorkspaceElementsLayerProps {
  elements: Element[];
  interaction: WorkspaceInteractionApi;
}

function useWorkspaceDragIds(interaction: WorkspaceInteractionApi) {
  const { elementDrag } = interaction;
  return new Set(
    elementDrag?.source === 'workspace' ? elementDrag.startPositions.map((p) => p.id) : [],
  );
}

/** 仅建立 stacking context，不改变 layout（与 PageSurface 内元素一致：layout 即世界坐标） */
function workspaceStackStyle(el: Element): CSSProperties {
  return {
    position: 'relative',
    zIndex: WORKSPACE_ELEMENTS_BASE_Z_INDEX + (el.layout.zIndex ?? 0),
  };
}

/**
 * 工作区根元素（世界坐标）。
 * 渲染约定与 PageSurface 相同：element.layout 即父系坐标，dragOffset 由 useElementDrag 提供。
 */
export function WorkspaceElementsLayer({ elements, interaction }: WorkspaceElementsLayerProps) {
  const components = getBuiltinElementComponents();
  const draggingWsIds = useWorkspaceDragIds(interaction);
  const {
    elementDrag,
    dragOffset,
    pageDragPreviewOnWorkspace,
    handleWorkspaceElementMouseDown,
    hideSelectionChrome,
    selectedElementIds,
    isPresentationMode,
  } = interaction;

  const pageEscapePreview = pageDragPreviewOnWorkspace ?? [];

  const roots = elements
    .filter((el) => el.interaction.visible !== false && !el.layout.parentId)
    .sort((a, b) => a.layout.zIndex - b.layout.zIndex);

  if (roots.length === 0 && pageEscapePreview.length === 0) return null;

  const bindWorkspaceMouseDown = (el: Element) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPresentationMode) return;
    handleWorkspaceElementMouseDown(e, el);
  };

  return (
    <>
      {pageEscapePreview
        .slice()
        .sort((a, b) => a.layout.zIndex - b.layout.zIndex)
        .map((el) => {
          const Comp = components[el.type as keyof typeof components];
          if (!Comp) return null;
          return (
            <div
              key={`page-escape-${el.id}`}
              style={workspaceStackStyle(el)}
              data-workspace-element={el.id}
              data-coordinate-space="world"
            >
              <Comp
                element={el as never}
                selected={false}
                dragOffset={{ dx: 0, dy: 0 }}
                onSelect={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          );
        })}
      {roots.map((el) => {
        if (el.type === 'text' && interaction.editingTextId === el.id) {
          return null;
        }
        const isDragging = draggingWsIds.has(el.id);
        const offset = isDragging && elementDrag ? dragOffset : { dx: 0, dy: 0 };
        const selected =
          !hideSelectionChrome && selectedElementIds.includes(el.id);
        const shell = (
          <div
            key={el.id}
            style={workspaceStackStyle(el)}
            data-workspace-element={el.id}
            data-coordinate-space="world"
          >
            {el.type === 'text' ? (
              <TextElementView
                element={el as TextElement}
                selected={selected}
                dragOffset={offset}
                onSelect={(e) => e.stopPropagation()}
                onMouseDown={bindWorkspaceMouseDown(el)}
                onStartEdit={
                  isPresentationMode
                    ? undefined
                    : (id, opts) => interaction.beginTextEdit(id, opts)
                }
              />
            ) : (
              (() => {
                const Comp = components[el.type as keyof typeof components];
                if (!Comp) return null;
                return (
                  <Comp
                    element={el as never}
                    zoom={interaction.zoom}
                    selected={selected}
                    dragOffset={offset}
                    onSelect={(e) => e.stopPropagation()}
                    onMouseDown={bindWorkspaceMouseDown(el)}
                  />
                );
              })()
            )}
          </div>
        );
        return shell;
      })}
    </>
  );
}
